import React, { useMemo, useState } from 'react';

import { Heading, ListingCard, SecondaryButtonInline } from '../../../components';

import css from '../ListingPage.module.css';
import classNames from 'classnames';
import { useGetListingsByProviderWithImagesQuery } from '../../../api/store/apiSlice';
import { find, isEmpty, sampleSize } from 'lodash';
import { LISTING_CATEGORIES } from '../../EditListingPage/listingTypeConfig';
import { MAX_MOBILE_SCREEN_WIDTH } from '../../ProfilePage/ProfilePage';
import { slice } from 'lodash/array';
import { DialogContent, DialogTitle } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CustomisedDialog from '../../../components/Dialog/Dialog';
import { HStack, Text } from '@chakra-ui/react';

const groupByListingCategory = otherListings => {
  if (isEmpty(otherListings)) return {};
  return otherListings.reduce((groupedListings, item) => {
    const category = item.attributes.publicData.categoryLevel1;
    if (!groupedListings[category]) {
      groupedListings[category] = [];
    }

    groupedListings[category].push(item);
    return groupedListings;
  }, {});
};

const RelatedListings = ({
  listingsToDisplay,
  listings,
  listingsContainerClasses,
  numberOfListingsToDisplay = 2,
  setIsOtherListingsModalOpen,
  setSelectedSector,
  listingGridClassname,
  inModal = false,
}) => {
  const category = listingsToDisplay[0]?.attributes?.publicData?.categoryLevel1;

  const truncatedListings = useMemo(() => slice(listingsToDisplay, 0, numberOfListingsToDisplay), [
    listingsToDisplay,
  ]);

  const listingsOnShow = inModal ? listingsToDisplay : truncatedListings;

  const listingGridClassnames = listingGridClassname || css.listingsGrid;

  return (
    <div className={listingsContainerClasses}>
      <ul className={listingGridClassnames}>
        {listingsOnShow.map(l => {
          const correspondingImages = listings?.data?.included || [];

          const providerFirstImage = find(correspondingImages, image => {
            return l?.relationships?.images?.data[0]?.id?.uuid === image.id.uuid;
          });

          return (
            <li className={css.listing} key={l.id.uuid}>
              <ListingCard
                listing={{ ...l, images: [providerFirstImage] }}
                showPrice={false}
                showFavouriteIcon={false}
                isSearchPage={false}
                showDropIn={false}
              />
            </li>
          );
        })}
      </ul>
      {listingsToDisplay?.length > numberOfListingsToDisplay && !inModal && (
        <SecondaryButtonInline
          className={css.viewAllButton}
          onClick={() => {
            setSelectedSector(category);
            setIsOtherListingsModalOpen(true);
          }}
        >
          {`Show all ${listingsToDisplay.length} ${
            category === LISTING_CATEGORIES.CLASS.id
              ? 'classes'
              : category === LISTING_CATEGORIES.PERSONAL_TRAINER.id
              ? 'personal trainers'
              : 'listings'
          }`}
        </SecondaryButtonInline>
      )}
    </div>
  );
};

const SectionOtherListingsMaybe = props => {
  const { currentListing } = props;

  const [isOtherListingsModalOpen, setIsChildListingsModalOpen] = useState(false);
  const [selectedSector, setSelectedSector] = useState(LISTING_CATEGORIES.CLASS.id);
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;

  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;

  const numberOfListingsToDisplay = isMobileLayout ? 2 : 3;

  const providerId = currentListing.author.id.uuid;

  const {
    data: listings = [],
    isLoading,
    isError,
    error,
  } = useGetListingsByProviderWithImagesQuery(providerId, {
    skip: !!!providerId,
    refetchOnMountOrArgChange: true,
  });

  const sortedListings = useMemo(() => {
    if (listings?.data?.data) {
      const otherListings = listings.data.data.filter(l => l.id?.uuid !== currentListing?.id?.uuid);
      return {
        otherListings: otherListings,
        listingsToDisplay: sampleSize(otherListings, numberOfListingsToDisplay),
      };
    }

    return {
      otherListings: [],
      listingsToDisplay: [],
    };
  }, [currentListing]);

  const groupedChildListings = groupByListingCategory(sortedListings.otherListings);

  const childClasses = useMemo(() => groupedChildListings[LISTING_CATEGORIES.CLASS.id], [
    sortedListings,
  ]);
  const childPersonalTrainers = useMemo(
    () => groupedChildListings[LISTING_CATEGORIES.PERSONAL_TRAINER.id],
    [sortedListings]
  );

  const listingsContainerClasses = classNames(css.listingsContainer, {});

  if (listings?.data?.data?.length <= 1) {
    return <></>;
  }

  if (!currentListing.author) {
    return null;
  }

  if (currentListing?.attributes?.publicData?.parentListingId == null) {
    if (isEmpty(childClasses) && isEmpty(childPersonalTrainers)) {
      return;
    }

    const onClose = () => {
      setIsChildListingsModalOpen(false);
    };

    const ModalNavBar = () => (
      <div className={css.tabNavBar}>
        {childClasses?.length > 0 && (
          <div className={css.tab}>
            <button
              key={LISTING_CATEGORIES.CLASS.id}
              className={LISTING_CATEGORIES.CLASS.id === selectedSector ? css.active : css.inactive}
              onClick={() => setSelectedSector(LISTING_CATEGORIES.CLASS.id)}
            >
              <HStack>
                {LISTING_CATEGORIES.CLASS.id === selectedSector
                  ? LISTING_CATEGORIES.CLASS.icon
                  : LISTING_CATEGORIES.CLASS.iconUnselected}
                <Text
                  fontWeight={
                    LISTING_CATEGORIES.CLASS.id === selectedSector ? 'semibold' : 'medium'
                  }
                  m={0}
                >
                  Classes
                </Text>
              </HStack>
            </button>
          </div>
        )}
        {childPersonalTrainers?.length > 0 && (
          <div className={css.tab}>
            <button
              key={LISTING_CATEGORIES.PERSONAL_TRAINER.id}
              className={
                LISTING_CATEGORIES.PERSONAL_TRAINER.id === selectedSector
                  ? css.active
                  : css.inactive
              }
              onClick={() => setSelectedSector(LISTING_CATEGORIES.PERSONAL_TRAINER.id)}
            >
              <HStack>
                {LISTING_CATEGORIES.PERSONAL_TRAINER.id === selectedSector
                  ? LISTING_CATEGORIES.PERSONAL_TRAINER.icon
                  : LISTING_CATEGORIES.PERSONAL_TRAINER.iconUnselected}
                <Text
                  fontWeight={
                    LISTING_CATEGORIES.PERSONAL_TRAINER.id === selectedSector
                      ? 'semibold'
                      : 'medium'
                  }
                  m={0}
                >
                  Personal Trainers
                </Text>
              </HStack>
            </button>
          </div>
        )}
      </div>
    );

    const ChildListingsModal = (
      <CustomisedDialog
        open={isOtherListingsModalOpen}
        onClose={onClose}
        fullScreen={isMobileLayout}
        fullWidth
        className={css.childListingsModal}
      >
        <div className={css.modalCloseButtonContainer}>
          <CloseRoundedIcon onClick={onClose} />
        </div>
        <DialogTitle className={css.modalHeader}>
          <Text fontWeight="semibold" textStyle="xl" color={'almostBlack'} mt={0}>
            {currentListing?.attributes?.title}
          </Text>
          <ModalNavBar />
        </DialogTitle>
        <DialogContent style={{ paddingTop: '1.5rem' }}>
          <RelatedListings
            listingsToDisplay={
              selectedSector === LISTING_CATEGORIES.CLASS.id ? childClasses : childPersonalTrainers
            }
            listings={listings}
            listingsContainerClasses={listingsContainerClasses}
            numberOfListingsToDisplay={childClasses?.length}
            setIsChildListingsModalOpen={setIsChildListingsModalOpen}
            setSelectedSector={setSelectedSector}
            listingGridClassname={css.listingsInModal}
            inModal={true}
          />
        </DialogContent>
      </CustomisedDialog>
    );

    return (
      <div>
        {ChildListingsModal}
        <section id="child-listings" className={css.sectionOtherListings}>
          {!isEmpty(childClasses) && (
            <div>
              <Heading as="h2" rootClassName={css.sectionHeadingWithExtraMargin}>
                {`Classes at ${currentListing?.author?.attributes?.profile?.displayName ||
                  'this Provider'}`}
              </Heading>
              <RelatedListings
                listingsToDisplay={childClasses}
                listings={listings}
                listingsContainerClasses={listingsContainerClasses}
                numberOfListingsToDisplay={numberOfListingsToDisplay}
                setIsOtherListingsModalOpen={setIsChildListingsModalOpen}
                setSelectedSector={setSelectedSector}
                inModal={false}
              />
            </div>
          )}
          {!isEmpty(childPersonalTrainers) && (
            <div>
              <Heading as="h2" rootClassName={css.sectionHeadingWithExtraMargin}>
                {`Personal Trainers at ${currentListing?.author?.attributes?.profile?.displayName ||
                  'this Provider'}`}
              </Heading>
              <RelatedListings
                listingsToDisplay={childPersonalTrainers}
                listings={listings}
                listingsContainerClasses={listingsContainerClasses}
                numberOfListingsToDisplay={numberOfListingsToDisplay}
                setIsOtherListingsModalOpen={setIsChildListingsModalOpen}
                setSelectedSector={setSelectedSector}
                inModal={false}
              />
            </div>
          )}
        </section>
      </div>
    );
  }

  return (
    <section id="child-listings" className={classNames(css.sectionOtherListings)}>
      <Heading as="h2" rootClassName={css.sectionHeadingWithExtraMargin}>
        More from {currentListing?.author?.attributes?.profile?.displayName || 'this Provider'}
      </Heading>
      <RelatedListings
        listingsToDisplay={sortedListings.listingsToDisplay}
        listings={listings}
        listingsContainerClasses={listingsContainerClasses}
        numberOfListingsToDisplay={numberOfListingsToDisplay}
        setIsOtherListingsModalOpen={setIsChildListingsModalOpen}
        setSelectedSector={setSelectedSector}
        inModal={false}
      />
    </section>
  );
};

export default SectionOtherListingsMaybe;
